import { useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { isAxiosError } from 'axios'
import { CheckCircle2, XCircle, ShieldCheck, Info, Mail } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ErrorState,
  LoadingState,
  PageHeader,
  Textarea,
  useToast,
} from '@/components/ui'
import { useAuthStore } from '@/stores/auth.ts'

interface ApprovalToken {
  id: string
  status: 'pending' | 'approved' | 'rejected' | 'superseded'
  role: 'primary' | 'deputy'
  approverName: string
  approverEmail: string
  customerId: string
  customerName: string | null
  structureId: string
  tanssTicketId: string | null
  version: number
  createdAt: string
  respondedAt: string | null
  responseNote: string | null
  operations: Array<{ type: string; id: string; [key: string]: unknown }>
}

function describeOp(op: ApprovalToken['operations'][number]): string {
  switch (op.type) {
    case 'update_permissions':
      return `Berechtigung „${op['path']}": ${op['old_rights']} → ${op['new_rights']}`
    case 'create_folder':
      return `Neuer Ordner „${op['path']}"`
    case 'rename_folder':
      return `Ordner: „${op['old_path']}" → „${op['new_path']}"`
    case 'create_group':
      return `Neue Gruppe ${op['groupType']}_${op['name']}`
    case 'rename_group':
      return `Gruppe: „${op['old_name']}" → „${op['new_name']}"`
    case 'create_drive':
      return `Neues Laufwerk ${op['letter']}: („${op['label']}")`
    case 'warn_delete_folder':
      return `Löschung: „${op['path']}"`
    case 'warn_delete_group':
    case 'warn_delete_ou':
    case 'warn_delete_server':
      return `Löschung: „${op['name']}"`
    case 'warn_delete_drive':
      return `Löschung: ${op['letter']}: („${op['label']}")`
    default:
      return op.type
  }
}

export function ApprovalPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [note, setNote] = useState('')
  const [lastDecision, setLastDecision] = useState<'approved' | 'rejected' | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery<ApprovalToken>({
    queryKey: ['approval-token', token],
    queryFn: () => apiClient.get(`/it-structures/approvals/token/${token}`).then((r) => r.data),
    enabled: !!token,
    retry: 1,
  })

  const respondMut = useMutation({
    mutationFn: async (decision: 'approved' | 'rejected') => {
      const path = decision === 'approved'
        ? `/it-structures/approvals/${token}/approve`
        : `/it-structures/approvals/${token}/reject`
      await apiClient.post(path, { note: note.trim() || undefined })
      return decision
    },
    onSuccess: (decision) => {
      setLastDecision(decision)
      toast({
        title: decision === 'approved' ? 'Freigabe erteilt' : 'Freigabe abgelehnt',
        description: 'Auftraggeber und Ticket werden informiert.',
        tone: decision === 'approved' ? 'success' : 'warning',
      })
    },
    onError: (e: unknown) => {
      let msg = 'Fehler beim Senden'
      if (isAxiosError(e)) {
        const data = e.response?.data as { error?: string; message?: string } | undefined
        msg = data?.error ?? data?.message ?? e.message
      } else if (e instanceof Error) {
        msg = e.message
      }
      setSubmitError(msg)
    },
  })

  if (!isAuthenticated) {
    // Redirect to login, mit Ruecksprung-Pfad
    const back = encodeURIComponent(location.pathname)
    navigate(`/login?return=${back}`, { replace: true })
    return null
  }

  const emailMismatch = data
    && user
    && data.approverEmail.trim().toLowerCase() !== user.email.trim().toLowerCase()

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 space-y-4">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-brand-500" />
              Freigabe IT-Struktur
            </span>
          }
          description={data ? `${data.customerName ?? 'Kunde'} — v${data.version}` : 'Freigabe bearbeiten'}
        />

        {isLoading && <LoadingState label="Freigabe wird geladen …" />}

        {error && (
          <ErrorState
            title="Freigabe konnte nicht geladen werden"
            description={(error as Error).message}
          />
        )}

        {data && (
          <>
            {data.status !== 'pending' && (
              <Card className="border-blue-200 bg-blue-50">
                <Card.Body className="flex items-start gap-2 text-body text-blue-900">
                  <Info className="w-5 h-5 mt-0.5" />
                  <div>
                    <p className="font-semibold">
                      Diese Freigabe wurde bereits mit Status „{data.status}" abgeschlossen.
                    </p>
                    {data.responseNote && <p className="mt-1">Notiz: {data.responseNote}</p>}
                    {data.respondedAt && (
                      <p className="text-caption text-blue-700 mt-1">
                        Abgeschlossen am {new Date(data.respondedAt).toLocaleString('de-DE')}
                      </p>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {emailMismatch && (
              <ErrorState
                title="Dieser Freigabe-Link ist nicht für deinen Account"
                description={`Die Freigabe ist an ${data.approverEmail} gerichtet. Du bist eingeloggt als ${user.email}. Bitte mit dem richtigen Account anmelden.`}
              />
            )}

            <Card>
              <Card.Header>
                <Card.Title className="flex items-center gap-2 text-body">
                  <Mail className="w-4 h-4 text-brand-500" />
                  Empfänger
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <p className="text-body text-gray-900">
                  {data.approverName} <span className="text-gray-500">({data.approverEmail})</span>
                </p>
                {data.role === 'deputy' && (
                  <Badge size="sm" variant="warning" className="mt-2">Stellvertreter</Badge>
                )}
                {data.tanssTicketId && (
                  <p className="text-caption text-gray-500 mt-1">
                    TANSS-Ticket: #{data.tanssTicketId}
                  </p>
                )}
              </Card.Body>
            </Card>

            <Card>
              <Card.Header>
                <Card.Title className="text-body">
                  Änderungen, die deine Freigabe benötigen ({data.operations.length})
                </Card.Title>
              </Card.Header>
              <ul className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
                {data.operations.map((op, i) => (
                  <li key={`${op.type}-${op.id}-${i}`} className="px-4 py-3 flex items-start gap-2">
                    <Badge size="sm" variant="info">{op.type}</Badge>
                    <span className="text-body text-gray-800 font-mono break-all">{describeOp(op)}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {data.status === 'pending' && !emailMismatch && !lastDecision && (
              <Card>
                <Card.Header>
                  <Card.Title className="text-body">Entscheidung</Card.Title>
                </Card.Header>
                <Card.Body className="space-y-3">
                  <Textarea
                    label="Notiz (bei Ablehnung Pflicht)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional — Begründung oder Hinweis für den Auftraggeber"
                    rows={3}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="primary"
                      leadingIcon={<CheckCircle2 className="w-4 h-4" />}
                      loading={respondMut.isPending && respondMut.variables === 'approved'}
                      disabled={respondMut.isPending}
                      onClick={() => respondMut.mutate('approved')}
                    >
                      Freigeben
                    </Button>
                    <Button
                      variant="danger"
                      leadingIcon={<XCircle className="w-4 h-4" />}
                      loading={respondMut.isPending && respondMut.variables === 'rejected'}
                      disabled={respondMut.isPending || !note.trim()}
                      onClick={() => respondMut.mutate('rejected')}
                    >
                      Ablehnen
                    </Button>
                  </div>
                  {!note.trim() && (
                    <p className="text-caption text-gray-500">
                      Für die Ablehnung muss eine Notiz hinterlegt werden.
                    </p>
                  )}
                  {submitError && <ErrorState size="sm" title="Fehler" description={submitError} />}
                </Card.Body>
              </Card>
            )}

            {lastDecision && (
              <Card className={lastDecision === 'approved' ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}>
                <Card.Body className="flex items-start gap-2">
                  {lastDecision === 'approved' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  )}
                  <div>
                    <p className="text-body font-semibold">
                      {lastDecision === 'approved' ? 'Freigabe erteilt' : 'Freigabe abgelehnt'}
                    </p>
                    <p className="text-caption text-gray-600 mt-1">
                      Du kannst dieses Fenster jetzt schließen. Der Auftraggeber wird per Mail informiert.
                    </p>
                  </div>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  )
}
